import React, { useState, useEffect } from "react";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import Input from "@components/Input";
import { toast } from "react-toastify";
import { useLang } from "@src/hooks/useLang";
import { observer } from "mobx-react-lite";
import {
  syncYieldToDeere,
  getYieldDeereSyncStatus,
  YieldSyncStatus,
} from "@services/fieldsService";
import {
  getJohnDeereStatus,
  getJohnDeereSyncSettings,
  JohnDeereSyncSettings,
  safeDeereUrl,
} from "@services/johnDeereService";
import Loading from "@components/Loading";

interface IProps {
  fieldId: string;
  fieldName: string | null;
  seasonId: string | undefined;
  /** field.externalId — must start with `johndeere:` to enable sync. */
  externalId?: string | null;
  /** Avg yield in user's display units (just for the summary card). */
  avgYieldDisplay?: string;
  visible: boolean;
  onClose: () => void;
  /** Called after a successful sync so the parent can refresh its chip. */
  onSynced?: () => void;
}

const SyncYieldToDeereDialog: React.FC<IProps> = ({
  fieldId,
  fieldName,
  seasonId,
  externalId,
  avgYieldDisplay,
  visible,
  onClose,
  onSynced,
}) => {
  const lang = useLang();
  const jdLang = lang.johnDeere;
  // The layer is pushed in the ACCOUNT's sync units (Profile → John Deere),
  // the same units the nightly and mark-complete syncs use — not this
  // viewer's display units, which made a manual layer disagree with the next
  // automatic one. The request sends no units; the server resolves them.
  const [accountUnits, setAccountUnits] = useState<JohnDeereSyncSettings | null>(null);
  const unitsLabel = accountUnits ? `${accountUnits.weight}/${accountUnits.area}` : "—";

  const [statusLoading, setStatusLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [prior, setPrior] = useState<YieldSyncStatus | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [termsUrl, setTermsUrl] = useState<string | null>(null);

  const linked =
    !!externalId && externalId.startsWith("johndeere:") && externalId.split(":").length >= 3;

  useEffect(() => {
    if (!visible) return;
    setStatusLoading(true);
    getJohnDeereStatus()
      .then((s) => setConnected(s.connected))
      .catch(() => setConnected(false))
      .finally(() => setStatusLoading(false));
    getJohnDeereSyncSettings()
      .then(setAccountUnits)
      .catch(() => setAccountUnits(null));
  }, [visible]);

  useEffect(() => {
    if (!visible || !seasonId || !linked) {
      setPrior(null);
      setReplaceExisting(false);
      return;
    }
    getYieldDeereSyncStatus(fieldId, seasonId)
      .then((s) => {
        setPrior(s);
        // Default the replace checkbox to true when there's a prior sync —
        // most users want to overwrite, not stack copies.
        setReplaceExisting(!!s.mapLayerUri);
      })
      .catch(() => setPrior(null));
  }, [visible, seasonId, linked, fieldId]);

  const defaultTitle = `GreenGrowth Yield · ${(fieldName ?? "field").trim()}`;
  useEffect(() => {
    if (visible) {
      setTitle(defaultTitle);
      setText("");
      setTermsUrl(null);
    }
  }, [visible, defaultTitle]);

  const handleSync = async () => {
    if (!seasonId) {
      toast.error("Season not selected");
      return;
    }
    if (!linked) {
      toast.error(
        jdLang.fieldNotLinked,
      );
      return;
    }

    setSyncLoading(true);
    setTermsUrl(null);
    try {
      await syncYieldToDeere(fieldId, {
        seasonId,
        title: title.trim() || defaultTitle,
        text: text.trim() || undefined,
        replaceExisting,
      });
      toast.success(
        jdLang.yieldSyncQueued,
      );
      onSynced?.();
      onClose();
    } catch (e: any) {
      // Only https links on John Deere's domain reach an href (safeDeereUrl).
      const tl = safeDeereUrl((e as { termsLocation?: string }).termsLocation);
      const cu = safeDeereUrl((e as { connectionsUrl?: string }).connectionsUrl);
      if (tl) {
        setTermsUrl(tl);
        toast.info(
          jdLang.termsRequired,
        );
      } else if (cu) {
        toast.error(
          <span>
            Grant <strong>Locations</strong> and <strong>Map Layers</strong> at{" "}
            <a
              href={cu}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline" }}
            >
              connections.deere.com
            </a>{" "}
            to sync yield maps.
          </span>,
          { autoClose: false },
        );
      } else {
        toast.error(e?.message ?? "Sync failed");
      }
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <Dialog
      title={jdLang.syncYieldTitle}
      visible={visible}
      onClose={onClose}
      style={{ maxWidth: 520 }}
    >
      {statusLoading ? (
        <Loading />
      ) : !connected ? (
        <Column crossAxisSize="max">
          <Text size={14} color="#6B7280">
            {jdLang.connectToSync}
          </Text>
          <Row justifyContent="flex-end" style={{ marginTop: 16 }}>
            <Button onClick={onClose} kind="outline">
              {jdLang.close}
            </Button>
          </Row>
        </Column>
      ) : !linked ? (
        <Column crossAxisSize="max">
          <Text size={14} color="#6B7280">
            {jdLang.fieldNotLinked}
          </Text>
          <Row justifyContent="flex-end" style={{ marginTop: 16 }}>
            <Button onClick={onClose} kind="outline">
              {jdLang.close}
            </Button>
          </Row>
        </Column>
      ) : (
        <Column crossAxisSize="max" style={{ gap: 16 }}>
          <Text size={14} color="#6B7280">
            {jdLang.syncYieldDescription.replace("{name}", fieldName ?? fieldId)}
          </Text>

          {avgYieldDisplay && (
            <Text size={13} color="#6B7280">
              {jdLang.avgYieldLabel}: <strong>{avgYieldDisplay}</strong>
            </Text>
          )}

          <Text size={13} color="#6B7280">
            {jdLang.unitsLabel}: <strong>{unitsLabel}</strong>
          </Text>

          {prior?.mapLayerUri && (
            <div style={{ background: "#FEF7E0", padding: 12, borderRadius: 6 }}>
              <Text size={13} color="#7C5E00">
                {jdLang.priorSync}{" "}
                {prior.status === "QUEUED"
                  ? jdLang.priorSyncQueued
                  : prior.status === "INVALID" || prior.status === "NO_FILE_RESOURCE"
                    ? jdLang.priorSyncFailed
                    : ""}
              </Text>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={replaceExisting}
                  onChange={(e) => setReplaceExisting(e.target.checked)}
                />
                <Text size={13}>
                  {jdLang.replaceExisting}
                </Text>
              </label>
            </div>
          )}

          <div>
            <Text size={14} style={{ marginBottom: 4, fontWeight: 600 }}>
              {jdLang.layerTitleLabel}
            </Text>
            <Text size={12} color="#6B7280" style={{ marginBottom: 8 }}>
              {jdLang.layerTitleHint}
            </Text>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={defaultTitle}
              maxLength={120}
            />
          </div>

          <div>
            <Text size={14} style={{ marginBottom: 4, fontWeight: 600 }}>
              {jdLang.layerNotesLabel}
            </Text>
            <Text size={12} color="#6B7280" style={{ marginBottom: 8 }}>
              {jdLang.layerNotesHint}
            </Text>
            <Input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder=""
              maxLength={2000}
            />
          </div>

          {termsUrl && (
            <Column crossAxisSize="max">
              <Text size={14} color="#6B7280">
                {jdLang.acceptTerms}
              </Text>
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#68ad18", marginTop: 4 }}
              >
                {termsUrl}
              </a>
            </Column>
          )}

          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button onClick={onClose} kind="outline">
              {jdLang.cancel}
            </Button>
            <Button onClick={handleSync} disabled={syncLoading}>
              {syncLoading
                ? <Loading />
                : jdLang.syncYieldButton}
            </Button>
          </Row>
        </Column>
      )}
    </Dialog>
  );
};

export default observer(SyncYieldToDeereDialog);
